import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useRef, useState, useMemo, useCallback, useEffect, type FormEvent } from "react";
import type { Locale } from "date-fns";
import {
  format,
  formatDistanceToNow,
  isToday,
  isYesterday,
  startOfDay,
} from "date-fns";
import { Pencil, Trash2, Loader2 } from "lucide-react";
import { apiUrl } from "@/lib/api-client";
import { notify } from "@/lib/notify";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

function noteTimestamp(note: { updatedAt?: string; createdAt?: string }): string {
  return note.updatedAt || note.createdAt || "";
}

function sectionLabelForDay(
  day: Date,
  dateLocale: Locale,
  t: (k: string) => string,
): string {
  if (isToday(day)) return t("deal.today");
  if (isYesterday(day)) return t("deal.notesYesterday");
  return format(day, "PPP", { locale: dateLocale });
}

function groupNotesByDay(
  notes: any[],
  dateLocale: Locale,
  t: (k: string) => string,
): { dayKey: string; label: string; items: any[] }[] {
  const groups: { dayKey: string; label: string; items: any[] }[] = [];
  for (const n of notes) {
    const ts = noteTimestamp(n);
    if (!ts) continue;
    const d = startOfDay(new Date(ts));
    const dayKey = format(d, "yyyy-MM-dd");
    const label = sectionLabelForDay(d, dateLocale, t);
    const last = groups[groups.length - 1];
    if (!last || last.dayKey !== dayKey) {
      groups.push({ dayKey, label, items: [n] });
    } else {
      last.items.push(n);
    }
  }
  return groups;
}

export type DealNotesTabProps = {
  dealId: string;
  /** Already sorted newest-first (server); not re-sorted on client. */
  notes: any[] | undefined;
  notesLoading: boolean;
  addNote: {
    mutate: (body: string) => void;
    isPending: boolean;
  };
  dateLocale: Locale;
};

export function DealNotesTab({
  dealId,
  notes,
  notesLoading,
  addNote,
  dateLocale,
}: DealNotesTabProps) {
  const { t } = useTranslation();
  const formRef = useRef<HTMLFormElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [draft, setDraft] = useState("");
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);

  const dayGroups = useMemo(() => {
    const list = notes ?? [];
    return groupNotesByDay(list, dateLocale, t);
  }, [notes, dateLocale, t]);

  const submitNote = useCallback(() => {
    const body = draft.trim();
    if (!body || addNote.isPending) return;
    addNote.mutate(body);
    setDraft("");
    formRef.current?.reset();
  }, [draft, addNote]);

  const onFormSubmit = (e: FormEvent) => {
    e.preventDefault();
    submitNote();
  };

  const showSkeleton = notesLoading && notes === undefined;
  const hasAnyNotes = (notes?.length ?? 0) > 0;
  const showGlobalEmpty =
    notes !== undefined && (notes?.length ?? 0) === 0 && !notesLoading;
  const noteList = notes ?? [];

  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-border bg-card p-3 shadow-sm">
        <form
          ref={formRef}
          onSubmit={onFormSubmit}
          className="space-y-2"
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              submitNote();
            }
          }}
        >
          <Textarea
            ref={textareaRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={t("deal.newNotePlaceholder")}
            rows={2}
            disabled={addNote.isPending}
            className="min-h-[72px] resize-y text-sm"
            aria-label={t("deal.newNotePlaceholder")}
          />
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
            <p className="order-2 text-[11px] text-muted-foreground sm:order-1 sm:mr-auto">
              {t("deal.notesSendShortcut")}
            </p>
            <Button
              type="submit"
              size="sm"
              disabled={addNote.isPending || !draft.trim()}
              className="order-1 w-full sm:order-2 sm:w-auto"
            >
              {addNote.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("deal.loadingShort")}
                </>
              ) : (
                t("deal.addActivity")
              )}
            </Button>
          </div>
        </form>
      </div>

      {showSkeleton ? (
        <ul className="space-y-1.5" aria-busy="true">
          {[0, 1, 2, 3].map((i) => (
            <li key={i} className="rounded-lg border border-border bg-card px-3 py-2">
              <div className="space-y-2">
                <Skeleton className="h-3.5 w-40" />
                <Skeleton className="h-10 w-full" />
              </div>
            </li>
          ))}
        </ul>
      ) : showGlobalEmpty ? (
        <div className="rounded-lg border border-dashed border-border bg-muted/15 px-3 py-6 text-center">
          <p className="text-sm font-medium text-foreground">{t("deal.noNotesYet")}</p>
          <p className="mt-1 text-xs text-muted-foreground">{t("deal.notesEmptyHint")}</p>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="mt-3"
            onClick={() => textareaRef.current?.focus()}
          >
            {t("deal.notesEmptyCta")}
          </Button>
        </div>
      ) : dayGroups.length === 0 && noteList.length > 0 ? (
        <ul className="space-y-1.5">
          {noteList.map((n: any) => (
            <li key={n.id}>
              <DealNoteCard
                dealId={dealId}
                note={n}
                dateLocale={dateLocale}
                onDeleteRequest={() => setDeleteTargetId(n.id)}
              />
            </li>
          ))}
        </ul>
      ) : (
        <ul className="space-y-3">
          {dayGroups.map((group) => (
            <li key={group.dayKey} className="space-y-1.5">
              <div className="sticky top-0 z-10 -mx-0.5 bg-background/95 px-0.5 py-0.5 backdrop-blur supports-[backdrop-filter]:bg-background/80">
                <h3 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {group.label}
                </h3>
              </div>
              <ul className="space-y-1.5">
                {group.items.map((n: any) => (
                  <li key={n.id}>
                    <DealNoteCard
                      dealId={dealId}
                      note={n}
                      dateLocale={dateLocale}
                      onDeleteRequest={() => setDeleteTargetId(n.id)}
                    />
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      )}

      <NoteDeleteDialog
        dealId={dealId}
        noteId={deleteTargetId}
        open={!!deleteTargetId}
        onOpenChange={(open) => {
          if (!open) setDeleteTargetId(null);
        }}
      />
    </div>
  );
}

function NoteDeleteDialog({
  dealId,
  noteId,
  open,
  onOpenChange,
}: {
  dealId: string;
  noteId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const del = useMutation({
    mutationFn: async (id: string) => {
      const r = await fetch(apiUrl(`/api/crm/deals/${dealId}/notes`), {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ noteId: id }),
      });
      if (!r.ok) throw new Error("delete note failed");
      return r.json();
    },
    onSuccess: () => {
      onOpenChange(false);
      qc.invalidateQueries({ queryKey: ["deal-notes", dealId] });
      qc.invalidateQueries({ queryKey: ["deal", dealId] });
    },
    onError: (err) => {
      notify.error(
        t("deal.notesDeleteTitle"),
        err instanceof Error ? err.message : String(err),
      );
    },
  });

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t("deal.notesDeleteTitle")}</AlertDialogTitle>
          <AlertDialogDescription>{t("deal.notesDeleteDescription")}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={del.isPending}>{t("common.cancel")}</AlertDialogCancel>
          <AlertDialogAction
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            disabled={del.isPending || !noteId}
            onClick={(e) => {
              e.preventDefault();
              if (noteId) del.mutate(noteId);
            }}
          >
            {del.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin inline" />
                {t("common.loading")}
              </>
            ) : (
              t("common.delete")
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function DealNoteCard({
  dealId,
  note,
  dateLocale,
  onDeleteRequest,
}: {
  dealId: string;
  note: any;
  dateLocale: Locale;
  onDeleteRequest: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(note.body);

  useEffect(() => {
    if (!editing) setValue(note.body);
  }, [note.body, editing]);

  const displayName = note.editorName || note.authorName || note.authorEmail || "—";
  const ts = note.updatedAt || note.createdAt;
  const dateObj = ts ? new Date(ts) : null;
  const absoluteLabel =
    dateObj && !Number.isNaN(dateObj.getTime())
      ? format(dateObj, "PPpp", { locale: dateLocale })
      : "—";
  const relativeLabel =
    dateObj && !Number.isNaN(dateObj.getTime())
      ? formatDistanceToNow(dateObj, { addSuffix: true, locale: dateLocale })
      : "—";

  const upd = useMutation({
    mutationFn: async () => {
      const r = await fetch(apiUrl(`/api/crm/deals/${dealId}/notes`), {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ noteId: note.id, body: value }),
      });
      if (!r.ok) throw new Error("note update failed");
      return r.json();
    },
    onSuccess: () => {
      setEditing(false);
      qc.invalidateQueries({ queryKey: ["deal-notes", dealId] });
      qc.invalidateQueries({ queryKey: ["deal", dealId] });
    },
  });

  const cardShell = "rounded-lg border border-border bg-card px-3 py-2 shadow-sm";

  if (editing) {
    return (
      <div className={cardShell}>
        <div className="mb-1.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
          <span className="font-medium text-foreground">{displayName}</span>
          <span>·</span>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="cursor-default border-b border-dotted border-muted-foreground/40">
                {relativeLabel}
              </span>
            </TooltipTrigger>
            <TooltipContent side="bottom">{absoluteLabel}</TooltipContent>
          </Tooltip>
        </div>
        <Textarea
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="min-h-[88px] resize-y text-sm"
          autoFocus
        />
        <div className="mt-2 flex gap-2">
          <Button size="sm" onClick={() => upd.mutate()} disabled={upd.isPending || !value.trim()}>
            {upd.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : t("common.save")}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setEditing(false);
              setValue(note.body);
            }}
          >
            {t("common.cancel")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <article className={cardShell}>
      <header className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0">
            <span className="text-sm font-semibold leading-tight text-foreground">{displayName}</span>
            <Tooltip>
              <TooltipTrigger asChild>
                <time
                  dateTime={ts || undefined}
                  className="cursor-default text-xs leading-tight text-muted-foreground border-b border-dotted border-muted-foreground/40"
                >
                  {relativeLabel}
                </time>
              </TooltipTrigger>
              <TooltipContent side="bottom">{absoluteLabel}</TooltipContent>
            </Tooltip>
          </div>
          <p className="mt-1.5 whitespace-pre-wrap text-sm leading-snug text-foreground">{note.body}</p>
        </div>
        <div className="flex shrink-0 gap-0.5">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-muted-foreground hover:text-foreground"
            title={t("deal.editLabel")}
            aria-label={t("deal.editLabel")}
            onClick={() => setEditing(true)}
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-muted-foreground hover:text-destructive"
            title={t("deal.deleteLabel")}
            aria-label={t("deal.deleteLabel")}
            onClick={onDeleteRequest}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </header>
    </article>
  );
}
